/**
 * ============================================================================
 * Admin Files Page
 * ============================================================================
 * 
 * This page allows administrators to:
 * - Download reference file templates
 * - Upload filled reference files to populate the database
 * 
 * ============================================================================
 */

import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { trpc } from "@/lib/trpc";
import { Download, FileSpreadsheet, FileText, Upload } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";
import UploadProgress, { UploadProgressProps } from "@/components/UploadProgress";
import UploadSummary from "@/components/UploadSummary";

const referenceFiles = [
  {
    id: "fe_master",
    name: "FE Master File",
    description: "Emission factors database (all scopes and categories)",
    icon: FileSpreadsheet,
    downloadUrl: "/api/files/fe_master.xlsx",
    size: "~200 KB",
    uploadEnabled: true,
  },
  {
    id: "mapping",
    name: "Mapping File",
    description: "Activity to emission factor mapping",
    icon: FileSpreadsheet,
    downloadUrl: "/api/files/mapping.xlsx",
    size: "~30 KB",
    uploadEnabled: true,
  },
  {
    id: "jobs",
    name: "Jobs Reference File",
    description: "Sectors, subsectors, and activities classification (EN/FR/AR)",
    icon: FileSpreadsheet,
    downloadUrl: "/api/files/jobs.xlsx",
    size: "~50 KB",
    uploadEnabled: true,
  },
  {
    id: "geo_units",
    name: "Geo Units File",
    description: "Countries and regions classification (EN/FR/AR)",
    icon: FileSpreadsheet,
    downloadUrl: "/api/files/geo_units.xlsx",
    size: "~20 KB",
    uploadEnabled: true,
  },
  {
    id: "benchmark",
    name: "Benchmark Data (ADEME)",
    description: "French companies emissions benchmark data by sector, size, and region",
    icon: FileSpreadsheet,
    downloadUrl: "/api/files/benchmark_ademe.csv",
    size: "~50 MB",
    uploadEnabled: true,
  },
];

export default function AdminFiles() {
  const [uploadingFile, setUploadingFile] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<UploadProgressProps | null>(null);
  const fileInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  
  // Get trpc utils for refetching stats
  const utils = trpc.useUtils();

  const uploadFeMasterMutation = trpc.upload.feMaster.useMutation({
    onSuccess: (data) => {
      toast.success(`✅ FE Master uploaded: ${data.inserted} rows inserted`);
      setUploadingFile(null);
      // Invalidate calculator factors cache to recalculate counters
      utils.calculator.getFactors.invalidate();
    },
    onError: (error) => {
      toast.error(`❌ Upload failed: ${error.message}`);
      setUploadingFile(null);
    },
  });

  const uploadMappingMutation = trpc.upload.mapping.useMutation({
    onSuccess: (data) => {
      toast.success(`✅ Mapping uploaded: ${data.inserted} rows inserted`);
      setUploadingFile(null);
      // Invalidate calculator factors cache to recalculate counters
      utils.calculator.getFactors.invalidate();
    },
    onError: (error) => {
      toast.error(`❌ Upload failed: ${error.message}`);
      setUploadingFile(null);
    },
  });

  const uploadJobsMutation = trpc.upload.jobs.useMutation({
    onSuccess: (data) => {
      toast.success(`✅ Jobs uploaded: ${data.inserted} rows inserted`);
      setUploadingFile(null);
    },
    onError: (error) => {
      toast.error(`❌ Upload failed: ${error.message}`);
      setUploadingFile(null);
    },
  });

  const uploadGeoUnitsMutation = trpc.upload.geoUnits.useMutation({
    onSuccess: (data) => {
      toast.success(`✅ Geo Units uploaded: ${data.inserted} rows inserted`);
      setUploadingFile(null);
    },
    onError: (error) => {
      toast.error(`❌ Upload failed: ${error.message}`);
      setUploadingFile(null);
    },
  });

  const uploadBenchmarkMutation = trpc.upload.benchmark.useMutation({
    onSuccess: (data) => {
      toast.success(`✅ Benchmark data uploaded: ${data.inserted} rows inserted across ${data.tables} tables`);
      setUploadingFile(null);
      utils.files.stats.invalidate();
    },
    onError: (error) => {
      toast.error(`❌ Upload failed: ${error.message}`);
      setUploadingFile(null);
    },
  });

  const handleDownload = (file: typeof referenceFiles[0]) => {
    toast.info(`Downloading ${file.name}...`);
    
    const link = document.createElement('a');
    link.href = file.downloadUrl;
    link.download = file.downloadUrl.split('/').pop() || 'file.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success(`${file.name} downloaded successfully`);
  };

  const handleUploadClick = (fileId: string) => {
    fileInputRefs.current[fileId]?.click();
  };

  const handleFileChange = async (fileId: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const isExcel = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');
    const isCsv = file.name.endsWith('.csv');
    
    if (fileId === 'benchmark' && !isCsv) {
      toast.error('Please upload a CSV file (.csv) for benchmark data');
      return;
    }
    
    if (fileId !== 'benchmark' && !isExcel) {
      toast.error('Please upload an Excel file (.xlsx or .xls)');
      return;
    }

    setUploadingFile(fileId);
    
    // Initialize progress
    setUploadProgress({
      fileName: file.name,
      status: "parsing",
      progress: 10,
    });

    try {
      // Read file as base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target?.result as string;
        const base64Data = base64.split(',')[1]; // Remove data:...;base64, prefix

        // Update progress: parsing complete
        setUploadProgress({
          fileName: file.name,
          status: "uploading",
          progress: 30,
        });

        // Estimate total rows (rough estimate: 1KB ≈ 10 rows)
        const estimatedRows = Math.round((base64Data.length * 0.75) / 100);
        
        // Call appropriate mutation based on fileId
        try {
          let result;
          if (fileId === 'fe_master') {
            result = await uploadFeMasterMutation.mutateAsync({ fileData: base64Data, fileName: file.name });
          } else if (fileId === 'mapping') {
            result = await uploadMappingMutation.mutateAsync({ fileData: base64Data, fileName: file.name });
          } else if (fileId === 'jobs') {
            result = await uploadJobsMutation.mutateAsync({ fileData: base64Data, fileName: file.name });
          } else if (fileId === 'geo_units') {
            result = await uploadGeoUnitsMutation.mutateAsync({ fileData: base64Data, fileName: file.name });
          } else if (fileId === 'benchmark') {
            result = await uploadBenchmarkMutation.mutateAsync({ fileData: base64Data, fileName: file.name });
          }
          
          // Update progress: complete
          setUploadProgress({
            fileName: file.name,
            status: "complete",
            progress: 100,
            totalRows: result?.inserted || 0,
          });
          
          // Refetch stats to update the summary table
          utils.upload.stats.invalidate();
          
          // Clear progress after 3 seconds
          setTimeout(() => setUploadProgress(null), 3000);
        } catch (error: any) {
          setUploadProgress({
            fileName: file.name,
            status: "error",
            progress: 0,
            error: error.message || "Unknown error",
          });
          
          // Clear progress after 5 seconds
          setTimeout(() => setUploadProgress(null), 5000);
        }
      };
      
      reader.onerror = () => {
        setUploadProgress({
          fileName: file.name,
          status: "error",
          progress: 0,
          error: "Failed to read file",
        });
        setUploadingFile(null);
        setTimeout(() => setUploadProgress(null), 5000);
      };
      
      reader.readAsDataURL(file);
    } catch (error: any) {
      setUploadProgress({
        fileName: file.name,
        status: "error",
        progress: 0,
        error: error.message || "Unknown error",
      });
      setUploadingFile(null);
      setTimeout(() => setUploadProgress(null), 5000);
    }

    // Reset input
    event.target.value = '';
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Reference Files</h1>
          <p className="text-muted-foreground">
            Download templates and upload filled reference files to populate the database
          </p>
        </div>

        {/* Upload Progress */}
        {uploadProgress && (
          <div className="mb-6">
            <UploadProgress {...uploadProgress} />
          </div>
        )}

        {/* Database Summary */}
        <UploadSummary />

        {/* Files Grid */}
        <div className="grid gap-6 md:grid-cols-2">
          {referenceFiles.map((file) => (
            <Card key={file.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <file.icon className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{file.name}</CardTitle>
                      <CardDescription className="text-xs mt-1">
                        {file.size}
                      </CardDescription>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  {file.description}
                </p>
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleDownload(file)}
                    variant="outline"
                    className="flex-1"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download Template
                  </Button>
                  {file.uploadEnabled && (
                    <>
                      <input
                        ref={(el) => { fileInputRefs.current[file.id] = el; }}
                        type="file"
                        accept={file.id === 'benchmark' ? '.csv' : '.xlsx,.xls'}
                        className="hidden"
                        onChange={(e) => handleFileChange(file.id, e)}
                      />
                      <Button
                        onClick={() => handleUploadClick(file.id)}
                        disabled={uploadingFile === file.id}
                        className="flex-1 bg-gradient-mypa hover:opacity-90"
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        {uploadingFile === file.id ? 'Uploading...' : 'Upload Data'}
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Info Section */}
        <Card className="bg-muted/50">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="h-5 w-5" />
              About Reference Files
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-muted-foreground">
            <p>
              <strong>FE Master File:</strong> The complete database of emission factors organized by scope
              (1, 2, 3) and category. Each factor includes the conversion rate (kgCO2e per unit).
              Upload your filled Excel file to populate the database.
            </p>
            <p>
              <strong>Mapping File:</strong> Links activities to their corresponding emission factors.
              This file determines which emission factor is used for each type of activity.
              Upload to create the mappings for your company.
            </p>
            <p>
              <strong>Jobs File:</strong> Contains the hierarchical classification of economic sectors,
              subsectors, and activities. Used for company profiling and activity categorization.
              (Upload disabled - table not yet implemented in Supabase)
            </p>
            <p>
              <strong>Geo Units File:</strong> Contains countries and regions with their codes and hierarchical
              organization. Used for geographic filtering and reporting.
              (Upload disabled - table not yet implemented in Supabase)
            </p>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}

